/**
 * What changed, told to the people using it (2026-09-25).
 *
 * Vladimir: *"a release note page and release notes modal that will show only
 * new changes that user did not see from last time — only minor version update
 * and above, no patch updates there"*.
 *
 * ## How a release gets here
 *
 * `APP_VERSION` is the version running. A release that changes what a person
 * can see or do bumps the MINOR (2.1.0 → 2.2.0) and adds its entry at the top
 * of `RELEASES`, stamped `shipped` with the moment of writing it; a fix bumps
 * the PATCH (2.2.0 → 2.2.1) and adds nothing — patches are never announced.
 * `releases.test.ts` refuses a minor bump that forgot its entry, an entry that
 * is not a minor or a major, and a `shipped` in the future.
 *
 * ## Who is told
 *
 * Only people who really skipped something (2026-09-28): never somebody new —
 * see `unseenReleases`.
 *
 * Written for the reader, in the words the app uses: what they can now do,
 * never how it was built.
 */
export const APP_VERSION = '2.14.0';

export interface Release {
  /** Always `x.y.0`: only minors and majors are announced. */
  version: string;
  /**
   * When it went out, to the second, in UTC — `2026-09-26T13:33:01Z`. It is
   * compared with the moment a person was first back, so a day is not precise
   * enough: write the time you write the entry, which is never later than the
   * release reaches anybody (2026-09-28).
   */
  shipped: string;
  title: string;
  changes: string[];
}

/** Newest first. */
export const RELEASES: Release[] = [
  {
    version: '2.14.0',
    shipped: '2026-10-14T09:28:02Z',
    title: 'The Calendar as a time grid, and one meeting once',
    changes: [
      'The Calendar is a time grid: hours down the side, each meeting as tall as it lasts, and meetings at the same time side by side. A meeting opens its details — the meeting, its series, its state and the switch. On a phone, meetings at the same time sit side by side under “at the same time”.',
      'A meeting is listed once, and gets one notetaker, even when its series was split in Google Calendar and colleagues’ calendars carry it under different ids.',
      'Send the notetaker works for anybody the meeting invites, not only for the person whose calendar it came from.',
      'A meeting where nothing was recorded says what really happened: one the notetaker was never let into says “Couldn’t get in” and is in your lists again, and one somebody sent away says who and when.',
    ],
  },
  {
    version: '2.13.0',
    shipped: '2026-10-08T15:22:07Z',
    title: 'A calendar, and only the meetings you turn on',
    changes: [
      'Meetings has a Calendar: a week at a time, a column a day, every meeting on it — the ones that happened and the ones to come. A title opens the meeting, the line under it opens its series, and a switch on each meeting still to come turns the notetaker on or off for it. On a phone the days stack one under another.',
      'Settings → Join my meetings has a third choice: Only meetings I turn on. The notetaker then comes only to a meeting you turn on with Record this one, or to a series you switch on from its page.',
      'A meeting where nothing was recorded says why: nobody came, nobody let the notetaker in, somebody removed it, or nothing came of it. A meeting nobody came to is no longer listed as recorded.',
      'When somebody says “Ptitim” in passing, the notetaker no longer answers what was not meant for it: it stays quiet, or says it was not sure and asks to be asked again.',
      'A meeting’s brief carries open items only from earlier meetings with the same people, never from your unrelated ones — and it opens for meetings you are invited to through a colleague’s calendar.',
      'Send a meeting’s note — the full note, or one of its write-ups — to a person on Slack, from you, in your own direct messages with them: Copy or export → Send the full note to a person on Slack, or Send to a person under a write-up. It needs your own Slack connected, and never goes through the Ptitim bot.',
      'On a phone, switches look like switches again — they had been squeezed into a dot.',
      'A meeting you are invited to through a Google group is yours even when it reached Ptitim through the calendar of a colleague who is not in that group: once anybody in the group has their calendar connected, its people are on the meeting — in Coming up, on the Calendar, and granted the recording.',
    ],
  },
  {
    version: '2.12.0',
    shipped: '2026-10-08T12:44:05Z',
    title: 'Collections, and the meetings you are invited to',
    changes: [
      'Channels in Ptitim are now Collections — a group of people and the meetings added to it — so they are never mistaken for Slack channels.',
      'Coming up lists the meetings you are invited to even when they came in through a colleague’s calendar, so you can find and open them before and during the call.',
      'A meeting’s Record tab is now Decisions: its decisions, open questions and risks. The video is on the Walkthrough tab.',
      'From a meeting, “Open …” in its series menu takes you to the series.',
      'Where you pick a Slack channel, Ptitim says how to make a private one appear: type /invite @Ptitim in it.',
      'Claude for work: the Connectors steps say that only an owner of your Claude organisation can add Ptitim.',
    ],
  },
  {
    version: '2.11.0',
    shipped: '2026-10-08T10:01:19Z',
    title: 'A series’ settings, in three groups',
    changes: [
      'A series’ page groups its settings: Recording (whether and how the notetaker comes), Notes (the default template, more write-ups, the languages) and Sharing (the Slack channel, sending the note there, Ptitim channels, Asana). Who usually comes sits beside its meetings.',
      'The Slack channel list shows every private channel the app has been invited to — in a big workspace some were missing.',
      'A series named on a meeting that has not happened yet keeps its name everywhere, is in the Series list, and its settings open.',
    ],
  },
  {
    version: '2.10.0',
    shipped: '2026-10-08T08:18:01Z',
    title: 'The transcript as it happens, and a series’ own note',
    changes: [
      'During a call, a meeting’s page opens on its transcript, and each line appears at the top as soon as it is said — no more waiting several seconds for the page to catch up. Your notes are a tab away.',
      'The transcript always reads newest first, during the call and after it. Nothing scrolls on its own: a new line appears above the one before it, where you are already looking.',
      'A series can have its own note: choose a Default template in the series’ settings, and every meeting of it is written up that way and opens on it. The full note is always a click away.',
      'Turn on Send it to Slack and, after every meeting, that note goes to the series’ Slack channel in the template’s own shape — and the message follows every change made to the note afterwards.',
      'A series’ settings can be changed before its first meeting is recorded: open the series from the meeting’s page.',
    ],
  },
  {
    version: '2.9.0',
    shipped: '2026-09-30T21:04:26Z',
    title: 'Your company’s picture on the notetaker',
    changes: [
      'The notetaker’s camera picture in a call is now dark and quiet, like the video tiles around it — “Recording”, or “Paused” while it is paused.',
      'Administrators can put the company’s own pictures in their place, one while recording and one while paused: Settings → Recording defaults → The notetaker in the call. Any picture is fitted without cropping, and used from the next meeting on.',
    ],
  },
  {
    version: '2.8.0',
    shipped: '2026-09-30T06:38:30Z',
    title: 'Handed over and dropped, the same everywhere',
    changes: [
      'Hand an action to somebody else and Slack follows: Post to Slack offers it as theirs and tags them, and a message already in a channel or a direct message is rewritten to name the new owner.',
      'Dropped means nobody will do it, so a dropped action leaves every list — the meeting, Open, Closed recently, write-ups, Slack messages. Until you leave the page it stays where it was, crossed out, with Undo.',
      'A Done button in a Slack message sent before a hand-over closes the action where it is now.',
      'What’s new appears only for people who were already using Ptitim when something changed — never on somebody’s first days.',
    ],
  },
  {
    version: '2.7.0',
    shipped: '2026-09-26T13:33:01Z',
    title: 'Talk to the notetaker',
    changes: [
      'Say “Ptitim, screenshot” in a meeting and the moment is kept: the picture is taken from the recording after the call and shown on the meeting’s Walkthrough tab, under the video.',
      'Say “Ptitim, create an Asana task” — with the task, or right after it was discussed — and it is made in your Asana, with the conversation around it. The link arrives in the meeting chat.',
      'Ask “Ptitim, …” anything about your meetings and get a short answer in the meeting chat, for everyone in the call — it reads only what you can open, and asking out loud is you choosing to share it.',
      'It listens until you stop talking, somebody else speaks, or you say “stop”. Russian works too: “Птитим, скриншот”.',
      'Administrators can let the notetaker say a few short phrases out loud (Settings → Organisation → Notetaker speaks). Off unless turned on; it always answers in the chat.',
    ],
  },
  {
    version: '2.6.0',
    shipped: '2026-09-26T11:11:40Z',
    title: 'A free month, then an agreement',
    changes: [
      'Every account has a free month from the day it was made. A small line under the header counts the days left.',
      'When it is over, the notetaker stops coming to your meetings, and Ptitim stops answering questions and writing things up on request — templates, a note written again, an imported meeting history. Everything already recorded stays yours to read, search and export.',
      'A meeting is still recorded when somebody in it can record. Meetings ahead that will not be say so on Meetings, days before.',
      'To keep going, your company agrees it with us — the line under the header says whom to write to. An agreement covers everybody in your workspace.',
      'Ptitim is for teams: a new account needs a work Google account. Accounts that already exist are not affected.',
    ],
  },
  {
    version: '2.5.0',
    shipped: '2026-09-25T18:04:40Z',
    title: 'Everything stays where you left it',
    changes: [
      'Done, Got it and Drop no longer make a commitment vanish: it stays in its place, crossed out, with Undo right where Done was. It moves to Closed recently when you switch away from the window or open another page.',
      'Closed recently follows the filters at the top of Open, shows the newest first, five at a time — the rest one click away.',
      'Filters are kept in the page address like tabs are: reload, go back, or send a link, and the filter is still on.',
      'Every select in the app is now the app’s own: it looks like the rest of Ptitim, and long lists — people, series — have a search box.',
      'A page you open starts at its top; Back takes you to where you were.',
    ],
  },
  {
    version: '2.4.0',
    shipped: '2026-09-25T17:16:58Z',
    title: 'What you type counts',
    changes: [
      'The note is written from what you type during the meeting as well as from what was said. What you wrote down is treated as what mattered: it is never dropped, and the rest is built around it.',
      'Your notes use the same editor as the full note — headings, lists, action items — and keep themselves as you type.',
      'Typed something after the note was written? On What you typed, ✦ Write the note again with these.',
      'Every tab stays where you left it: reload the page or come back to it and the same tab is open — a meeting’s, People’s, Open’s and the rest. A link you share opens on that tab too.',
    ],
  },
  {
    version: '2.3.0',
    shipped: '2026-09-25T16:43:43Z',
    title: 'Edit notes where you read them',
    changes: [
      'The full note is a document now: click anywhere in it and type. No Edit button and no Save — it keeps itself a moment after you stop, and says so.',
      'Type Markdown and it becomes the thing itself: ## for a heading, - for a list, 1. for a numbered one, [] for an action item, **bold** and the rest. Or select some text for the same as buttons, and ⌘K for a link.',
      'Tick an action item in the note and it stays ticked.',
      'Write-ups made with a template are edited the same way. They say who changed them, and writing one again over somebody’s changes asks first.',
      'Paste Markdown — from a chat answer, a doc, a model — and it arrives formatted. Copy from a note and it leaves as Markdown.',
    ],
  },
  {
    version: '2.2.0',
    shipped: '2026-09-25T15:21:44Z',
    title: 'Templates',
    changes: [
      'Write a meeting up with a template — a short digest for a channel, a sprint review, a standup, a 1:1 and more. It sits beside the full note on the Full note tab; the note itself never changes.',
      'Copy a write-up, or send it to a Slack channel as a message everyone there can read — a Ptitim account not needed.',
      'Make your own templates, for yourself or for everyone in the workspace: what the write-up is for, and its sections, each with what goes under it. Profile menu → Templates.',
      'A series can be written up after every meeting and posted to its channel on its own: on the series page, “Written up as”.',
    ],
  },
  {
    version: '2.1.0',
    shipped: '2026-09-25T13:25:51Z',
    title: 'Who came and went',
    changes: [
      'The transcript shows who joined the call and who left it, at the moment it happened. People arriving together are one line; the full list is a click away.',
      'Release notes — this page — and a short note like this one when something new arrives. You only ever see what you have not seen.',
      'People moved into your profile menu, so the header keeps the four places you use every day.',
    ],
  },
  {
    version: '2.0.0',
    shipped: '2026-09-21T12:00:00Z',
    title: 'Ptitim 2.0',
    changes: [
      'A notetaker joins the meetings on your calendar, records them and writes the note.',
      'Every commitment has an owner and, when one was said, a due date. The next meeting closes what it hears was done, and Done works straight from Slack.',
      'Knowledge that stays current: decisions, terms and facts, confirmed with one tap and replaced when a later meeting changes them.',
      'Topics follow one subject across meetings, series and people.',
      'Your day in Slack every morning, notes to channels and people, tasks to Asana.',
      'The recording in chapters, on the Walkthrough tab.',
      'Everything in the language you read, whatever language the meeting was in.',
    ],
  },
];

/**
 * What everybody has seen by definition: the version that was running before
 * there were notes to read. Nobody is told about the product they already use.
 */
const BEFORE_NOTES = '2.0.0';

/** `[major, minor, patch]`. */
export function versionParts(version: string): [number, number, number] {
  const [major = 0, minor = 0, patch = 0] = version.split('.').map((n) => Number(n) || 0);
  return [major, minor, patch];
}

/** Whether `a` is a later version than `b`. */
export function isNewer(a: string, b: string): boolean {
  const x = versionParts(a);
  const y = versionParts(b);
  for (let i = 0; i < 3; i += 1) if (x[i] !== y[i]) return x[i] > y[i];
  return false;
}

/** Only minors and majors — a patch is a fix, and fixes are not news. */
export function announced(releases: Release[] = RELEASES): Release[] {
  return releases.filter((r) => versionParts(r.version)[2] === 0);
}

/**
 * The notes this person has not seen, newest first.
 *
 * Vladimir, 2026-09-28: *"Make sure we don't show new users modal with release
 * notes. We need to show only to those who really skipped some changes"*.
 * `returnedAt` is when they were first back, twelve hours or more after
 * signing up (the api stamps it) — null while they are new, and a new person
 * is told nothing: the whole product is new to them. After that, only what
 * shipped once they were back and is later than the last notes they saw. A
 * release that came out while somebody was still new is part of the product
 * they started with, however long ago they signed up.
 *
 * ⚠ It used to compare the day a release shipped with the day the account was
 * made. Somebody who signed up and left the same day was told every release
 * since on their return — and a release dated by a local day already ahead of
 * UTC's could be shown to somebody who signed up after it went out.
 */
export function unseenReleases(
  seen: string | null,
  returnedAt: string | null,
  releases: Release[] = RELEASES,
): Release[] {
  if (!returnedAt) return [];
  const last = seen ?? BEFORE_NOTES;
  const back = Date.parse(returnedAt);
  return announced(releases).filter(
    (r) => isNewer(r.version, last) && Date.parse(r.shipped) > back,
  );
}

/** The newest release there is notes for — what "seen them all" records. */
export function newestAnnounced(releases: Release[] = RELEASES): string {
  return announced(releases)[0]?.version ?? BEFORE_NOTES;
}
